import { createContext, ReactNode, useContext, useEffect, useMemo, useReducer, useState } from 'react'

import { rows } from '@/db/database'
import { activeOffers, applyOffers, PendingReward, pendingRewards, PosOffer } from '@/services/OfferEngine'

import { storage } from '@/lib/storage'
import { roundQty } from '@/services/itemPrompt'
import { calculateTotals } from '@/services'
import { memberDiscount, priceLines } from '@/services/PricingService'
import { PosModel } from '@/types'

import { useOptionalRegisterContext } from './RegisterContext'

interface CartState {
  lines: PosModel.CartLine[]
  customer?: PosModel.Customer
  discount?: PosModel.Discount
  /** Round-off typed by the cashier (0–0.99). */
  adjustment?: number
  /** The line the cashier touched last; `+`/`-` and the highlight follow it. */
  activeId?: string
}

type Action =
  | { type: 'add'; product: PosModel.Product; quantity?: number }
  | { type: 'setQty'; id: string; quantity: number }
  | { type: 'remove'; id: string }
  | { type: 'lineDiscount'; id: string; discount?: PosModel.Discount }
  | { type: 'customer'; customer?: PosModel.Customer }
  | { type: 'discount'; discount?: PosModel.Discount }
  | { type: 'adjustment'; adjustment: number }
  | { type: 'load'; state: CartState }
  | { type: 'clear' }

const STORAGE_KEY = 'iws-pos:cart'

/** Sales continue past the recorded stock (it goes negative and is flagged); stock in is an online action. */
const clampQty = (_product: PosModel.Product, qty: number) => Math.max(0, roundQty(qty))

function reducer(state: CartState, action: Action): CartState {
  switch (action.type) {
    case 'add': {
      const existing = state.lines.find((l) => l.product._id === action.product._id)
      const qty = (existing?.quantity ?? 0) + (action.quantity ?? 1)
      const lines = existing
        ? state.lines.map((l) => (l === existing ? { ...l, quantity: clampQty(l.product, qty) } : l))
        : [...state.lines, { product: action.product, quantity: clampQty(action.product, qty) }]
      return { ...state, lines: lines.filter((l) => l.quantity > 0), activeId: action.product._id }
    }
    case 'setQty':
      return {
        ...state,
        lines: state.lines
          .map((l) => (l.product._id === action.id ? { ...l, quantity: clampQty(l.product, action.quantity) } : l))
          .filter((l) => l.quantity > 0),
        activeId: action.id,
      }
    case 'remove':
      return { ...state, lines: state.lines.filter((l) => l.product._id !== action.id), activeId: undefined }
    case 'lineDiscount':
      return {
        ...state,
        lines: state.lines.map((l) => (l.product._id === action.id ? { ...l, discount: action.discount } : l)),
      }
    case 'customer':
      return { ...state, customer: action.customer }
    case 'discount':
      return { ...state, discount: action.discount }
    case 'adjustment':
      return { ...state, adjustment: action.adjustment }
    case 'load':
      return action.state
    case 'clear':
      return { lines: [] }
  }
}

interface CartContextValue extends CartState {
  totals: PosModel.Totals
  /** Offer saving per cart line (by product id), for showing on the line; totals already include it. */
  offerSavings: Record<string, number>
  /** The member-tier discount in use: the customer's tier, while the cashier has set no order discount (§7.3). */
  memberDiscount?: PosModel.Discount
  /** Rewards earned but not in the cart yet (buy X get Y), for the cashier to add. */
  rewards: PendingReward[]
  add: (product: PosModel.Product, quantity?: number) => void
  setQty: (id: string, quantity: number) => void
  remove: (id: string) => void
  setLineDiscount: (id: string, discount?: PosModel.Discount) => void
  setCustomer: (customer?: PosModel.Customer) => void
  setDiscount: (discount?: PosModel.Discount) => void
  setAdjustment: (adjustment: number) => void
  load: (state: CartState) => void
  clear: () => void
}

const CartContext = createContext<CartContextValue | null>(null)

/** The sale in progress. Saved on every change so a reload or crash never loses a half-rung basket. */
export const CartProvider = ({ children }: { children: ReactNode }) => {
  // The sign-in screens render without a register; there is no sale to total there.
  const tax_rate = useOptionalRegisterContext()?.tax_rate ?? 0
  const [state, dispatch] = useReducer(reducer, undefined, () => storage.read<CartState>(STORAGE_KEY, { lines: [] }))

  useEffect(() => storage.write(STORAGE_KEY, state), [state])

  // Offers on the device, re-read each minute so a sync's changes show without a reload (O3).
  const [offers, setOffers] = useState<PosOffer[]>([])
  useEffect(() => {
    const read = () => rows('offers').toArray().then((list) => setOffers(list as unknown as PosOffer[])).catch(() => setOffers([]))
    read()
    const timer = setInterval(read, 60_000)
    return () => clearInterval(timer)
  }, [])

  const value = useMemo<CartContextValue>(() => {
    // The same engine as the sale itself, so the total shown is the total charged.
    const live = activeOffers(offers)
    // The customer's price book first, then offers on those prices (§7.1, §13.1).
    const forCustomer = priceLines(state.lines, state.customer)
    const priced = applyOffers(forCustomer, live)
    const offerSavings: Record<string, number> = {}
    for (const l of priced) {
      if (l.offer_id && l.discount) offerSavings[l.product._id] = (offerSavings[l.product._id] ?? 0) + l.discount.value
    }
    return {
      ...state,
      totals: calculateTotals(priced, state.discount ?? memberDiscount(state.customer), tax_rate, state.adjustment ?? 0),
      offerSavings,
      memberDiscount: state.discount ? undefined : memberDiscount(state.customer),
      rewards: pendingRewards(forCustomer, live),
      add: (product, quantity) => dispatch({ type: 'add', product, quantity }),
      setQty: (id, quantity) => dispatch({ type: 'setQty', id, quantity }),
      remove: (id) => dispatch({ type: 'remove', id }),
      setLineDiscount: (id, discount) => dispatch({ type: 'lineDiscount', id, discount }),
      setCustomer: (customer) => dispatch({ type: 'customer', customer }),
      setDiscount: (discount) => dispatch({ type: 'discount', discount }),
      setAdjustment: (adjustment) => dispatch({ type: 'adjustment', adjustment }),
      load: (next) => dispatch({ type: 'load', state: next }),
      clear: () => dispatch({ type: 'clear' }),
    }
  }, [state, tax_rate, offers])

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export const useCart = () => {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart must be used inside CartProvider')
  return ctx
}
